import React, { useEffect, useRef } from "react";
import {
  FaBriefcase,
  FaUsers,
  FaChartLine,
  FaHandshake,
  FaGlobe,
  FaLightbulb,
  FaAward,
  FaRocket,
} from "react-icons/fa";

const PlacementSection = () => {
  const sectionRef = useRef(null);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    if (cardRef.current) observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-green-500/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10" ref={sectionRef}>
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-green-600 via-blue-500 to-green-600 bg-clip-text text-transparent animate-gradient">
              Placement
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Bridging the gap between academia and industry
          </p>
        </div>

        <div
          ref={cardRef}
          className="group opacity-0 transition-all duration-700 transform translate-y-8 hover:-translate-y-2 hover:shadow-2xl rounded-2xl shadow-lg overflow-hidden bg-gradient-to-br from-green-50 via-white to-blue-50 border-t-4 border-green-500"
        >
          <div className="p-8 md:p-10">
            <div className="flex items-start gap-4 mb-8">
              <div className="bg-gradient-to-br from-green-600 to-emerald-600 p-3 rounded-xl shadow-lg">
                <FaBriefcase className="text-white text-xl" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Our Placement Cell</h3>
                <p className="text-gray-600 leading-relaxed">
                  The Placement Cell constantly keeps in touch with the Industry to ascertain the technology advancement 
                  and plans well in advance for bridging any gap in addition to what students learn in their curriculum. 
                  The success of the Placement Cell is evident from our alumni occupying positions at the best opted 
                  companies in the country. The students are provided the platform for developing interpersonal, 
                  technical and business skills, so that they can face the global challenges of today and the future.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center hover:shadow-md transition-all">
                <FaUsers className="text-3xl text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-800">100+</div>
                <div className="text-xs text-gray-500">Recruiters</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center hover:shadow-md transition-all">
                <FaChartLine className="text-3xl text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-800">95%</div>
                <div className="text-xs text-gray-500">Placement Record</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center hover:shadow-md transition-all">
                <FaGlobe className="text-3xl text-green-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-800">50+</div>
                <div className="text-xs text-gray-500">Global Opportunities</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm rounded-xl p-4 text-center hover:shadow-md transition-all">
                <FaAward className="text-3xl text-blue-600 mx-auto mb-2" />
                <div className="text-2xl font-bold text-gray-800">₹6-12 LPA</div>
                <div className="text-xs text-gray-500">Average Package</div>
              </div>
            </div>
            <div className="mb-8">
              <h4 className="font-semibold text-gray-800 text-lg mb-4 flex items-center gap-2">
                <FaLightbulb className="text-yellow-500" />
                Student Development
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FaHandshake className="text-green-500" />
                  <span>Interpersonal Skills</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FaBriefcase className="text-blue-500" />
                  <span>Technical Aptitude</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FaChartLine className="text-green-500" />
                  <span>Business Acumen</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FaGlobe className="text-blue-500" />
                  <span>Global Readiness</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FaUsers className="text-green-500" />
                  <span>Industry Interactions</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600 text-sm">
                  <FaRocket className="text-blue-500" />
                  <span>Entrepreneurship Support</span>
                </div>
              </div>
            </div>
            <div className="bg-white/40 rounded-xl p-5 border border-gray-100">
              <p className="text-gray-600 italic text-sm">
                "The placement cell's constant industry connect and training programs helped me secure a dream job 
                at one of the top MNCs. Grateful for the holistic development!"
              </p>
              <p className="text-gray-500 text-xs mt-2">— Alumni, Batch 2024</p>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-up {
          from {
            opacity: 0;
            transform: translateY(2rem);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fade-up 0.6s ease-out forwards;
        }
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% auto;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </section>
  );
};

export default PlacementSection;